import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { SalesService } from '@/services/sales.service';
import { OutreachService } from '@/services/outreach.service';
import { useAuth } from '@/contexts/AuthContext';
import { format } from 'date-fns';
import { Button, Card, Badge, LoadingSpinner, EmptyState, FormInput, FormSelect, FormTextarea } from '@/components/common';
import { Building2, ArrowLeft, Edit, Mail, Phone, DollarSign, FileText, TrendingUp, Plus, Calendar, Activity } from 'lucide-react';

export const LeadDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { profile } = useAuth();
  const [showContactForm, setShowContactForm] = useState(false);
  const [contactMethod, setContactMethod] = useState<'email' | 'phone' | 'in_person' | 'linkedin' | 'other'>('email');
  const [contactOutcome, setContactOutcome] = useState<'no_response' | 'voicemail' | 'spoke' | 'interested' | 'not_interested' | 'callback_requested'>('no_response');
  const [contactNotes, setContactNotes] = useState('');

  const { data: lead, isLoading } = useQuery({
    queryKey: ['lead', id],
    queryFn: () => id ? SalesService.getLead(id) : null,
    enabled: !!id,
  });

  const { data: outreachHistory = [] } = useQuery({
    queryKey: ['outreach', id],
    queryFn: () => id ? OutreachService.getOutreachHistory(id) : [],
    enabled: !!id,
  });

  const contactMutation = useMutation({
    mutationFn: async () => {
      if (!id || !profile?.id) throw new Error('Missing required data');
      return OutreachService.recordContactAttempt(id, {
        method: contactMethod,
        outcome: contactOutcome,
        notes: contactNotes,
        contactedBy: profile.id,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lead', id] });
      queryClient.invalidateQueries({ queryKey: ['outreach', id] });
      setShowContactForm(false);
      setContactNotes('');
    },
  });

  if (isLoading) {
    return <LoadingSpinner text="Loading lead details..." fullScreen />;
  }

  if (!lead) {
    return (
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <EmptyState
          icon={Building2}
          title="Lead not found"
          description="The lead you're looking for doesn't exist."
          action={
            <Button variant="primary" icon={ArrowLeft} onClick={() => navigate('/sales')}>
              Back to Sales
            </Button>
          }
        />
      </div>
    );
  }

  const handleRecordContact = () => {
    contactMutation.mutate();
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-6">
        <Button
          variant="ghost"
          icon={ArrowLeft}
          onClick={() => navigate('/sales')}
          className="mb-4"
        >
          Back to Sales
        </Button>
        <Card>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-lg bg-gradient-to-br from-blue-100 to-blue-200 flex items-center justify-center">
                <Building2 className="w-8 h-8 text-blue-600" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">{lead.companyName}</h1>
                <div className="mt-2 flex items-center gap-2 flex-wrap">
                  <Badge variant="info" size="sm">{lead.contactName}</Badge>
                  <Badge variant="gray" size="sm">{lead.email}</Badge>
                  <Badge variant="success" size="sm" className="capitalize">{lead.status}</Badge>
                </div>
              </div>
            </div>
            <Button
              variant="primary"
              size="sm"
              icon={Edit}
              onClick={() => navigate(`/sales/leads/${id}/edit`)}
            >
              Edit Lead
            </Button>
          </div>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Lead Information */}
          <Card>
            <h2 className="text-xl font-semibold mb-6 flex items-center gap-2">
              <Building2 className="w-5 h-5 text-blue-600" />
              Lead Information
            </h2>
            <dl className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <dt className="text-sm font-medium text-gray-500 mb-1">Source</dt>
                <dd className="text-sm text-gray-900 font-medium">{lead.source}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500 mb-1 flex items-center gap-2">
                  <Phone className="w-4 h-4" />
                  Phone
                </dt>
                <dd className="text-sm text-gray-900">{lead.phone || 'N/A'}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500 mb-1 flex items-center gap-2">
                  <DollarSign className="w-4 h-4" />
                  Value
                </dt>
                <dd className="text-sm text-gray-900 font-semibold">
                  {lead.value ? `$${lead.value.toLocaleString()}` : 'N/A'}
                </dd>
              </div>
              <div className="col-span-2">
                <dt className="text-sm font-medium text-gray-500 mb-1 flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  Notes
                </dt>
                <dd className="text-sm text-gray-900 whitespace-pre-wrap bg-gray-50 p-4 rounded-lg">{lead.notes || 'No notes'}</dd>
              </div>
            </dl>
          </Card>

          {/* Outreach History */}
          <Card>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold flex items-center gap-2">
                <Activity className="w-5 h-5 text-blue-600" />
                Outreach History
              </h2>
              <Button
                variant="primary"
                size="sm"
                icon={Plus}
                onClick={() => setShowContactForm(!showContactForm)}
              >
                Record Contact
              </Button>
            </div>

            {showContactForm && (
              <Card className="mb-6 bg-gray-50">
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <FormSelect
                      label="Method"
                      options={[
                        { value: 'email', label: 'Email' },
                        { value: 'phone', label: 'Phone' },
                        { value: 'in_person', label: 'In Person' },
                        { value: 'linkedin', label: 'LinkedIn' },
                        { value: 'other', label: 'Other' },
                      ]}
                      value={contactMethod}
                      onChange={(e) => setContactMethod(e.target.value as any)}
                    />
                    <FormSelect
                      label="Outcome"
                      options={[
                        { value: 'no_response', label: 'No Response' },
                        { value: 'voicemail', label: 'Voicemail' },
                        { value: 'spoke', label: 'Spoke' },
                        { value: 'interested', label: 'Interested' },
                        { value: 'not_interested', label: 'Not Interested' },
                        { value: 'callback_requested', label: 'Callback Requested' },
                      ]}
                      value={contactOutcome}
                      onChange={(e) => setContactOutcome(e.target.value as any)}
                    />
                  </div>
                  <FormTextarea
                    label="Notes"
                    rows={3}
                    value={contactNotes}
                    onChange={(e) => setContactNotes(e.target.value)}
                    placeholder="Add notes about this contact attempt..."
                  />
                  <div className="flex justify-end gap-2">
                    <Button
                      variant="outline"
                      onClick={() => {
                        setShowContactForm(false);
                        setContactNotes('');
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="primary"
                      onClick={handleRecordContact}
                      isLoading={contactMutation.isPending}
                    >
                      Save Contact
                    </Button>
                  </div>
                </div>
              </Card>
            )}

            {outreachHistory.length === 0 ? (
              <p className="text-gray-500 text-sm text-center py-8">No outreach history yet</p>
            ) : (
              <div className="space-y-3">
                {outreachHistory.map((record) => (
                  <div key={record.id} className="border-l-4 border-blue-500 pl-4 py-3 bg-gray-50 rounded-r-lg hover:bg-gray-100 transition-colors">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="text-sm font-semibold text-gray-900">
                          {record.method.charAt(0).toUpperCase() + record.method.slice(1)} - {record.outcome.replace('_', ' ')}
                        </p>
                        <p className="text-xs text-gray-500 mt-1 flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {format(record.date, 'MMM d, yyyy h:mm a')}
                        </p>
                        {record.notes && (
                          <p className="text-sm text-gray-700 mt-2">{record.notes}</p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Quick Stats */}
          <Card>
            <h3 className="text-lg font-semibold mb-6 flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-blue-600" />
              Quick Stats
            </h3>
            <dl className="space-y-4">
              <div className="pb-4 border-b border-gray-200">
                <dt className="text-sm text-gray-500 mb-1">Contact Attempts</dt>
                <dd className="text-3xl font-bold text-gray-900">{outreachHistory.length}</dd>
              </div>
              <div className="pb-4 border-b border-gray-200">
                <dt className="text-sm text-gray-500 mb-1">Last Contact</dt>
                <dd className="text-sm text-gray-900 font-medium">
                  {outreachHistory[0] ? format(outreachHistory[0].date, 'MMM d, yyyy') : 'Never'}
                </dd>
              </div>
              <div>
                <dt className="text-sm text-gray-500 mb-1">Created</dt>
                <dd className="text-sm text-gray-900 font-medium">{format(lead.createdAt, 'MMM d, yyyy')}</dd>
              </div>
            </dl>
          </Card>
        </div>
      </div>
    </div>
  );
};

